import { type FunctionComponent, useContext, useState } from 'react';
import { type ProductVariantFieldsFragment } from '../../../__generated__/graphql-client-types';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { hasRestrictions } from '../../../helpers/product-helper/product.helper';
import { ClickableElement } from '../../buttons';
import { Drawer } from '../../common-components/drawer/drawer.component';
import { DropdownMenuSelect } from '../../common-components/dropdown-menu/dropdown-menu.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import ChevronDownIcon from '../../svgs/chevron-down.svg';
import ChevronRightIcon from '../../svgs/chevron-right.svg';
import WarningIcon from '../../svgs/warning.svg';
import { ProductPrice } from '../product-price/product-price.component';
import { ShippingEligibility } from '../shipping-eligibility/shipping-eligibility.component';

export type MobileVariantSelectorProps = {
	variants: ProductVariantFieldsFragment[];
	familyId: number;
	variantType?: string;
};

/**
 * MobileVariantSelector - renders a variant selector specific to mobile devices
 *
 * @param {MobileVariantSelectorProps} props - MobileSelector properties
 */
export const MobileVariantSelector: FunctionComponent<MobileVariantSelectorProps> = ({ variants, variantType = 'Finish', familyId }) => {
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const { selectedVariant } = useContext(VariantContext);

	const showDrawer = () => {
		setIsDrawerOpen(true);
	};

	const resolveCurrentVariantSelectedMessage = () => {
		if (selectedVariant) {
			return selectedVariant.title;
		} else {
			return `Choose ${variantType}`;
		}
	};

	const closeDrawerHandler = () => {
		setIsDrawerOpen(false);
	};

	return (
		<div className={'w-100 mt2'}>
			{isDrawerOpen && (
				<Drawer ariaLabel={`${variantType} Selector`} direction="right" onClose={closeDrawerHandler}>
					<>
						<div className="f4 b bb b--theme-grey-light h2">Select {variantType}</div>
						<div className={`list flex flex-wrap`} data-testid="swatches">
							{variants.map((current) => (
								<MobileVariant
									variant={current}
									key={current.id.toString()}
									showRestrictionWarning={hasRestrictions(current, familyId)}
									onSelection={closeDrawerHandler}
								/>
							))}
						</div>
					</>
				</Drawer>
			)}
			<MobileVariantButton onClick={showDrawer}>
				<div className="flex flex-row justify-between items-center w-100 lh-copy f5" data-testid="mv-select-btn">
					<span>{resolveCurrentVariantSelectedMessage()}</span>
					{isDrawerOpen ? <ChevronDownIcon className="f3" /> : <ChevronRightIcon className="f3" />}
				</div>
			</MobileVariantButton>
		</div>
	);
};
// This has slightly different styles than the secondary button
// The buttons border uses theme grey
// It has a selected state that uses theme grey dark(When it is the currently selected variant)
const MobileVariantButton: FunctionComponent<{
	onClick: () => void;
	isHighlighted?: boolean;
	children: string | JSX.Element | string[];
}> = ({ children, onClick, isHighlighted = false }) => {
	return (
		<div
			className={`ba br2 ph2 b--theme-grey-light hover-b--theme-primary active-b--theme-primary-darker ${
				isHighlighted ? 'b--theme-grey-darker' : 'b--theme-grey-light'
			}`}>
			<DropdownMenuSelect toggle={onClick} expanded={false}>
				{children}
			</DropdownMenuSelect>
		</div>
	);
};
/**
 * Mobile Single Variant
 */
const MobileVariant: FunctionComponent<{
	variant: ProductVariantFieldsFragment;
	showRestrictionWarning?: boolean;
	onSelection?: Function;
}> = ({ variant, showRestrictionWarning, onSelection }) => {
	const { selectedVariant, setSelectedVariant } = useContext(VariantContext);

	const { id, image, title, priceInfo, count } = variant;
	const isSelected = Boolean(selectedVariant && selectedVariant.id === id);

	function handleButtonClick() {
		setSelectedVariant(variant);
		if (onSelection) {
			onSelection();
		}
	}

	return (
		<ClickableElement
			ariaLabel={`Select ${title}`}
			onClick={handleButtonClick}
			testId={`${id}`}
			key={id}
			className={`flex flex-wrap w-100 mt3 ba br3 pa3 ${isSelected ? 'b--theme-primary bw1' : 'b--theme-grey-light'}`}>
			<>
				<div className="w-third">
					{showRestrictionWarning && <WarningIcon className="absolute z-1 f5 f4-ns ma1 theme-warning" />}
					<CloudinaryImage publicID={image.id} description={title} options={{ height: 100, width: 100 }} useDimensions={false} />
				</div>
				<div className="w-two-thirds pl3">
					<strong>{title}</strong>
					<div className="mt2 theme-primary">
						<ProductPrice {...priceInfo} />
					</div>
					<div className="mt2">{count === 0 ? 'Special Order' : `${count} in stock`}</div>
					<ShippingEligibility variant={variant} />
				</div>
			</>
		</ClickableElement>
	);
};
